import { useMemo } from 'react';

import { AgGridReact } from '@ag-grid-community/react';

import { SelectedData, SectionAndBomItemData } from '../types';

function isOnlyKitsWithComponentsSelected(tableDataMap: Map<number, SectionAndBomItemData>, selectedItems: SelectedData[]) {
    let kitsAndComponentSize = 0;
    const kitsAndComponentsMap = new Map<number, SelectedData[]>();

    for (let item of selectedItems) {
        if (item.is_kit) {
            kitsAndComponentsMap.set(item.id, [item]);
            kitsAndComponentSize++;
        }
    }

    for (let bodItem of tableDataMap.values()) {
        if (bodItem?.kit && kitsAndComponentsMap.has(bodItem.kit)) {
            kitsAndComponentsMap.get(bodItem.kit)?.push(bodItem as SelectedData);
            kitsAndComponentSize++;
        }
    }

    return { isOnlyKitsAndComponentsExists: kitsAndComponentSize === selectedItems.length, kitsAndComponentsMap };
}

export const useHideUnhideStatus = (selectedItems: SelectedData[], tableRef: React.RefObject<AgGridReact<any>>) => {
    const result = useMemo(() => {
        let status: 'hide' | 'un-hide' = 'un-hide';
        let selectedIds: number[] = [];

        // Preprocess tableData for fast lookups by id
        const tableDataMap = new Map();

        tableRef?.current?.api.forEachNode((rowNode) => {
            const { data } = rowNode;
            if (data?.groupPath?.length > 1) {
                const { is_hidden, groupPath, id, is_kit, kit } = data;
                tableDataMap.set(data.id, { is_hidden, groupPath, id, is_kit, kit });
            }
        });

        if (selectedItems.length && tableDataMap.size) {
            const selectedBodsMap = new Map<number, boolean>();
            const seenKitsAndComponents = new Map<
                number,
                {
                    kitData: SelectedData | null;
                    hideCount: number;
                    componentsLength: number;
                }
            >();
            const unhideSet = new Set<number>();
            const hideSet = new Set<number>();

            // Check if we have selected only kit(s) with all of its component(s)
            const { isOnlyKitsAndComponentsExists, kitsAndComponentsMap } = isOnlyKitsWithComponentsSelected(tableDataMap, selectedItems);
            // If true, we only need to check the hide/unhide status of the kit(s)
            if (isOnlyKitsAndComponentsExists) {
                for (let values of kitsAndComponentsMap.values()) {
                    if (values[0].is_hidden) {
                        values.forEach((item) => {
                            hideSet.add(item.id);
                        });
                    } else {
                        values.forEach((item) => {
                            unhideSet.add(item.id);
                        });
                    }
                }
            } else {
                // Else, Populate selectedBodsMap with items having a group path length > 1
                for (const item of selectedItems) {
                    if (item.groupPath.length > 1) {
                        selectedBodsMap.set(item.id, item.is_hidden);
                    }
                }

                // Process each item based on its group path length
                for (const item of selectedItems) {
                    if (item.groupPath.length <= 2) continue; // Skip non-component items early

                    const kitId = item.groupPath[1];

                    // if  kit  already exisits in the selectedBodsMap along with all of its components then remove the component's ids from selectedBodsMap.
                    if (selectedBodsMap.has(kitId)) {
                        const kitComponents = kitsAndComponentsMap.get(kitId);
                        if (kitComponents?.length) {
                            // get the hide- Unhide state of kit
                            // in kitComponents, the el at 0th index is kitData
                            const kitHideState = kitComponents[0].is_hidden;
                            kitComponents.forEach((component, index) => {
                                if (index) {
                                    // if hidden, add comps ids to hide set otherwise unhide set
                                    if (kitHideState) {
                                        hideSet.add(component.id);
                                    } else {
                                        unhideSet.add(component.id);
                                    }
                                    // remove these ids from selectedBodsMap
                                    selectedBodsMap.delete(component.id);
                                }
                            });
                        }
                        continue;
                    }

                    // Get kit data , hideCount & componentsLength, using cached data if available
                    let cachedObject = seenKitsAndComponents.get(kitId) || { kitData: null, hideCount: 0, componentsLength: 0 };

                    if (!cachedObject.kitData) {
                        cachedObject.kitData = tableDataMap.get(kitId);

                        for (let values of tableDataMap.values()) {
                            if (values.kit === kitId) {
                                cachedObject.componentsLength++;
                                if (values.is_hidden) {
                                    cachedObject.hideCount++;
                                }
                            }
                        }
                        seenKitsAndComponents.set(kitId, cachedObject);
                    }

                    // if kit is hidden, then we know that all the other components of this kit is also hidden. Or, if there is only one un-hidden component in the kit and we know that kit state is un-hidden and the currenty selected component has un-hidden state, we can add the kit id to selectedBods Map.
                    if (cachedObject.kitData?.is_hidden || (cachedObject.hideCount >= cachedObject.componentsLength - 1 && !item.is_hidden)) {
                        selectedBodsMap.set(kitId, cachedObject.kitData?.is_hidden || false);
                    }
                }

                // Separate hide/unhide sets based on selectedBodsMap entries
                for (const [bod, isHidden] of selectedBodsMap.entries()) {
                    if (isHidden) hideSet.add(bod);
                    else unhideSet.add(bod);
                }
            }

            if ((hideSet.size && unhideSet.size) || hideSet.size) {
                status = 'un-hide';
            } else {
                status = 'hide';
            }

            selectedIds = [...hideSet, ...unhideSet];
        }

        return { status, selectedIds, tableDataMap };
    }, [selectedItems, tableRef]);

    return result;
};
